import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  X,
  FloppyDisk,
  BookOpen,
  Wrench,
  ChatCircle,
  Envelope,
  PhoneTransfer,
  Globe,
  Hash,
  FileText,
  Info,
  Question,
  Trash,
  Plus,
  Gear,
  CaretUp,
  CaretDown,
  Clock,
  Calendar,
  Buildings
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/text-area';
import { Label } from '../ui/label';
import { getSupabaseClient } from '../../supabaseClient';
import { useBusinessType } from '../../context/BusinessContext';
import { cn } from '../../lib/utils';
import MentionInput from '../ui/MentionInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { delay: 0.1, duration: 0.2 } }
};

const modalVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.98 },
  visible: { 
    opacity: 1, 
    y: 0, 
    scale: 1,
    transition: { 
      duration: 0.3, 
      ease: [0.16, 1, 0.3, 1]
    }
  },
  exit: { 
    opacity: 0, 
    y: 20, 
    scale: 0.98,
    transition: { duration: 0.2, ease: "easeOut" }
  }
};

export const SimpleActionContentModal = ({ 
  isOpen, 
  onClose, 
  variant, 
  isInline = false 
}) => {
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [formData, setFormData] = useState({
    sms_message: '',
    email_subject: '',
    email_message: '',
    transfer_number: '',
    transfer_name: '',
    transfer_start_time: '09:00',
    transfer_end_time: '17:00',
    transfer_days: [0,1,2,3,4,5,6],
    api_endpoint: '',
    api_body: '',
    dtmf_number_to_press: '',
    note_fields_config: []
  });

  const [actionType, setActionType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [questions, setQuestions] = useState([]);
  const [categoryName, setCategoryName] = useState('');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load category name
  useEffect(() => {
    const loadCategoryName = async () => {
      if (!isOpen || !variant?.parentSetting?.category_id) return;

      try {
        const { data: categoryData, error: categoryError } = await supabase
          .from('brain_categories')
          .select('name')
          .eq('id', variant.parentSetting.category_id)
          .single();

        if (categoryError) throw categoryError;

        setCategoryName(categoryData?.name || '');
      } catch (error) {
        console.error('Error loading category name:', error);
        setCategoryName('');
      }
    };

    loadCategoryName();
  }, [isOpen, variant?.parentSetting?.category_id, supabase]);

  // Load questions from the parent action
  useEffect(() => {
    const loadQuestions = async () => {
      if (!isOpen || !variant?.settingsId) return;

      try {
        // Get the primary simple action for this settings_id to load questions
        const { data: simpleActions, error: saError } = await supabase
          .from('brain_simple_actions')
          .select('id')
          .eq('settings_id', variant.settingsId)
          .limit(1);

        if (saError) throw saError;

        if (simpleActions && simpleActions.length > 0) {
          const { data: questionsData, error: qError } = await supabase
            .from('brain_simple_actions_questions')
            .select('*')
            .eq('simple_action_id', simpleActions[0].id)
            .order('order');

          if (qError) throw qError;

          setQuestions(questionsData || []);
        }
      } catch (error) {
        console.error('Error loading questions:', error);
      }
    };

    loadQuestions();
  }, [isOpen, variant?.settingsId, supabase]);

  // Format questions for mention components
  const questionsForMention = questions.map((q, index) => ({
    id: q.id,
    text: `question_${index + 1}_response`
  }));

  // Initialize form data from variant
  useEffect(() => {
    if (isOpen && variant) {
      // Get action type from multiple possible sources
      let detectedActionType = '';

      if (variant.fullData && variant.fullData.type) {
        detectedActionType = variant.fullData.type;
      } else if (variant.parentSetting && variant.parentSetting.action_type) {
        detectedActionType = variant.parentSetting.action_type;
      } else if (variant.parentSetting && variant.parentSetting.fullData && variant.parentSetting.fullData.type) {
        detectedActionType = variant.parentSetting.fullData.type;
      } else if (variant.parentSetting && variant.parentSetting.original_type) {
        // Try original_type as fallback
        detectedActionType = variant.parentSetting.original_type;
      }

      setActionType(detectedActionType);

      if (variant.fullData) {
        const variantData = variant.fullData;
        setFormData({
          sms_message: variantData.sms_message || '',
          email_subject: variantData.email_subject || '',
          email_message: variantData.email_message || '',
          transfer_number: variantData.transfer_number || '',
          transfer_name: variantData.transfer_name || '',
          transfer_start_time: variantData.transfer_start_time || '09:00',
          transfer_end_time: variantData.transfer_end_time || '17:00',
          transfer_days: variantData.transfer_days || [0,1,2,3,4,5,6],
          api_endpoint: variantData.api_endpoint || '',
          api_body: variantData.api_body || '',
          dtmf_number_to_press: variantData.dtmf_number_to_press || '',
          note_fields_config: variantData.note_fields_config || []
        });
      }
    }
  }, [isOpen, variant]);

  const updateNoteFieldsConfig = (newFields) => {
    setFormData(prev => ({ ...prev, note_fields_config: newFields }));
    setHasUnsavedChanges(true);
    if (validationErrors.note_fields_tab_error) {
        setValidationErrors(prev => {
            const newErrors = {...prev};
            delete newErrors.note_fields_tab_error;
            return newErrors;
        });
    }
  };

  const handleSave = async () => {
    // New validation logic
    const errors = {};
    if (actionType === 'note') {
        if (!formData.note_fields_config || formData.note_fields_config.length === 0) {
            errors.note_fields_config = "At least one note field is required";
        } else {
            const fieldErrors = {};
            formData.note_fields_config.forEach((field, index) => {
                if (!field.question || !field.question.trim()) {
                    fieldErrors[field.temp_id || `new_${index}`] = "Field label is required";
                }
                if (field.type === 'enum' && (!field.options || field.options.length === 0)) {
                    fieldErrors[field.temp_id || `new_${index}`] = "At least one option is required for 'Choose One' fields.";
                }
            });
            if (Object.keys(fieldErrors).length > 0) {
                errors.note_fields_individual = fieldErrors;
            }
        }
    }

    if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        toast.error("Please fix the errors before saving.");
        return;
    }
    setValidationErrors({}); // Clear errors on successful validation

    setIsSubmitting(true);
    try {
      // Update only the specific grouping's action data
      const settingsId = variant.settingsId;
      const groupingId = variant.id;

      // Upsert the simple action row
      const { data: saRow, error: saErr } = await supabase
        .from('brain_simple_actions')
        .upsert({
          settings_id: settingsId,
          grouping_id: groupingId,
          action_type: actionType
        }, { onConflict: ['settings_id', 'grouping_id'] })
        .select('id')
        .single();

      if (saErr) throw saErr;

      // Save type-specific data
      await saveTypeSpecificData(saRow.id);

      toast.success('Action updated successfully');
      setHasUnsavedChanges(false);

      // Reset loading state BEFORE calling onClose for inline modals
      setIsSubmitting(false);

      // Always notify parent of successful save, but don't close modal for inline
      const saveResult = { 
        success: true,
        settingsId: settingsId, // Add settings ID for coverage analysis
        shouldClose: !isInline // Only close if not inline
      };

      onClose(saveResult);
    } catch (error) {
      console.error('Error saving action:', error);
      toast.error('Failed to save action');
      setIsSubmitting(false);
    }
  };

  const saveTypeSpecificData = async (simpleActionId) => {
    if (actionType === 'sms') {
      await supabase
        .from('brain_simple_actions_sms')
        .upsert({
          simple_action_id: simpleActionId,
          message: formData.sms_message,
          use_contact_phone: false
        }, { onConflict: 'simple_action_id' });
    } else if (actionType === 'email') {
      await supabase
        .from('brain_simple_actions_email')
        .upsert({
          simple_action_id: simpleActionId,
          subject: formData.email_subject,
          message: formData.email_message,
          use_contact_email: false
        }, { onConflict: 'simple_action_id' });
    } else if (actionType === 'transfer') {
      await supabase
        .from('brain_simple_actions_transfer_call')
        .upsert({
          simple_action_id: simpleActionId,
          phone_number: formData.transfer_number,
          name: formData.transfer_name,
          start_time: formData.transfer_start_time,
          end_time: formData.transfer_end_time,
          days_of_week: formData.transfer_days
        }, { onConflict: 'simple_action_id' });
    } else if (actionType === 'api') {
      await supabase
        .from('brain_simple_actions_api')
        .upsert({
          simple_action_id: simpleActionId,
          url: formData.api_endpoint,
          method: 'POST',
          body: formData.api_body ? JSON.parse(formData.api_body) : null
        }, { onConflict: 'simple_action_id' });
    } else if (actionType === 'dtmf') {
      await supabase
        .from('brain_simple_actions_dtmf')
        .upsert({
          simple_action_id: simpleActionId,
          number_to_press: formData.dtmf_number_to_press
        }, { onConflict: 'simple_action_id' });
    } else if (actionType === 'note') {
      const noteFieldsToSave = formData.note_fields_config || [];

      // Delete existing note fields for this simple_action_id before inserting new ones
      const { error: deleteErr } = await supabase
        .from('brain_simple_actions_fields')
        .delete()
        .eq('simple_action_id', simpleActionId);
      if (deleteErr) throw deleteErr;

      if (noteFieldsToSave.length > 0) {
        const tempIdToDbIdMap = {};

        // Pass 1: Insert core field data (without parent_field_id and criteria initially)
        const orderedFieldsToSave = noteFieldsToSave.map((f, idx) => ({ ...f, order: f.order ?? idx }));

        for (const field of orderedFieldsToSave) {
          const { data: fieldRow, error: fieldErr } = await supabase
            .from('brain_simple_actions_fields')
            .insert({
              simple_action_id: simpleActionId,
              question: field.question,
              type: field.type, 
              order: field.order
            })
            .select('id')
            .single();
          if (fieldErr) { console.error("Error inserting field", field, fieldErr); throw fieldErr; }
          tempIdToDbIdMap[field.temp_id] = fieldRow.id;

          if (field.type === 'enum' && field.options && field.options.length > 0) {
            const enumRecords = field.options.map(opt => ({
              simple_action_field_id: fieldRow.id,
              enum: opt
            }));
            const { error: enumErr } = await supabase
              .from('brain_simple_actions_fields_enum')
              .insert(enumRecords);
            if (enumErr) { console.error("Error inserting enums", field, enumErr); throw enumErr; }
          }
        }

        // Pass 2: Update parent_field_id and criteria
        for (const field of orderedFieldsToSave) {
          const currentDbId = tempIdToDbIdMap[field.temp_id];
          if (!currentDbId) {
            console.warn("Could not find DB ID for field in Pass 2:", field);
            continue;
          }

          let parentDbIdToSave = null;
          if (field.parent_field_temp_id) {
            parentDbIdToSave = tempIdToDbIdMap[field.parent_field_temp_id];
            if (!parentDbIdToSave) {
              console.warn(`Could not resolve parent_field_id for field ${field.question}`);
            }
          }

          // Only update if there's a parent or criteria to set
          if (parentDbIdToSave || field.criteria) {
            const updatePayload = {};
            if (parentDbIdToSave) updatePayload.parent_field_id = parentDbIdToSave;
            if (field.criteria) updatePayload.criteria = field.criteria;

            if (Object.keys(updatePayload).length > 0) {
              const { error: updateErr } = await supabase
                .from('brain_simple_actions_fields')
                .update(updatePayload)
                .eq('id', currentDbId);
              if (updateErr) { console.error("Error updating parent/criteria", field, updateErr); throw updateErr; }
            }
          }
        }
      }
    }
  };

  const getActionIcon = () => {
    switch (actionType) {
      case 'sms': return ChatCircle;
      case 'email': return Envelope;
      case 'transfer': return PhoneTransfer;
      case 'api': return Globe;
      case 'dtmf': return Hash;
      case 'note': return FileText;
      default: return Wrench;
    }
  };

  const getActionTitle = () => {
    switch (actionType) {
      case 'sms': return 'SMS Message';
      case 'email': return 'Email Message';
      case 'transfer': return 'Call Transfer';
      case 'api': return 'API Request';
      case 'dtmf': return 'DTMF Input';
      case 'note': return 'Note Fields';
      default: return 'Action';
    }
  };

  const renderActionFields = () => {
    switch (actionType) {
      case 'sms':
        return (
          <div className="space-y-5">
            {/* Message Preview */}
            <div className="space-y-1.5 sm:space-y-2">
              <h3 className="text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">Message Preview</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4">
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 sm:p-4 relative">
                  <p className="text-xs sm:text-sm text-gray-800 whitespace-pre-wrap">
                    {formData.sms_message || 'Your message will appear here'}
                  </p>
                  <div className="absolute -top-2 -left-2 bg-indigo-100 text-indigo-800 text-[9px] sm:text-[10px] px-1 sm:px-1.5 py-0.5 rounded">
                    Preview
                  </div>
                </div>
              </div>
            </div>

            {/* Message Content */}
            <div className="space-y-1.5 sm:space-y-2 pt-3 sm:pt-4 border-t border-gray-100">
              <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                <ChatCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-blue-500" />
                Text Message
                <span className="text-red-500 ml-0.5">*</span>
              </h3>
              <p className="text-[10px] sm:text-xs text-gray-500">
                This message will be sent when the tool is triggered{questions.length > 0 ? '. Use @question_1_response, @question_2_response, etc. to reference answers.' : ''}
              </p>

              <MentionInput
                id="sms_message"
                name="sms_message"
                value={formData.sms_message}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => ({ ...prev, sms_message: value }));
                  setHasUnsavedChanges(true);
                }}
                placeholder="Enter the message to send when this tool is triggered"
                className="min-h-[100px] sm:min-h-[120px] text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors"
                questions={questionsForMention}
                multiline={true}
              />

              <div className="flex justify-between items-center">
                <div className={cn(
                  "text-[10px] sm:text-xs", 
                  formData.sms_message.length > 160 ? "text-red-500" : "text-gray-500"
                )}>
                  {formData.sms_message.length}/160 characters
                </div>
              </div>
            </div>
          </div>
        );

      case 'email':
        return (
          <div className="space-y-5">
            {/* Email Preview */}
            <div className="space-y-1.5 sm:space-y-2">
              <h3 className="text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">Email Preview</h3>
              <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 sm:p-5">
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 sm:p-4 relative">
                  <div className="mb-4 border-b border-gray-100 pb-2">
                    <div className="flex items-center text-xs sm:text-sm text-gray-700 mb-1">
                      <span className="font-medium mr-1">From:</span>
                      <span>Your Business</span>
                    </div>
                    <div className="flex items-center text-xs sm:text-sm text-gray-700 mb-1">
                      <span className="font-medium mr-1">To:</span>
                      <span>Customer</span>
                    </div>
                    <div className="flex items-center text-xs sm:text-sm text-gray-900 font-medium">
                      <span className="mr-1">Subject:</span>
                      <span>{formData.email_subject || 'Your email subject will appear here'}</span>
                    </div>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-800 whitespace-pre-wrap">
                    {formData.email_message || 'Your email message will appear here'}
                  </p>

                  <div className="absolute -top-2 -left-2 bg-indigo-100 text-indigo-800 text-[9px] sm:text-[10px] px-1 sm:px-1.5 py-0.5 rounded">
                    Preview
                  </div>
                </div>
              </div>
            </div>

            {/* Email Subject */}
            <div className="space-y-1.5 sm:space-y-2 pt-3 sm:pt-4 border-t border-gray-100">
              <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                <Envelope className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-indigo-500" />
                Email Subject
                <span className="text-red-500 ml-0.5">*</span>
              </h3>
              <p className="text-[10px] sm:text-xs text-gray-500">
                Enter the subject line for your email{questions.length > 0 ? '. Use @question_1_response, @question_2_response, etc. to reference answers.' : ''}
              </p>

              <MentionInput
                id="email_subject"
                name="email_subject"
                value={formData.email_subject}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => ({ ...prev, email_subject: value }));
                  setHasUnsavedChanges(true);
                }}
                placeholder="Enter email subject line"
                className="h-9 sm:h-10 text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors"
                questions={questionsForMention}
              />
            </div>

            {/* Email Message */}
            <div className="space-y-1.5 sm:space-y-2">
              <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                <ChatCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-indigo-500" />
                Email Message
                <span className="text-red-500 ml-0.5">*</span>
              </h3>
              <p className="text-[10px] sm:text-xs text-gray-500">
                Enter the email message body{questions.length > 0 ? '. Use @question_1_response, @question_2_response, etc. to reference answers.' : ''}
              </p>

              <MentionInput
                id="email_message"
                name="email_message"
                value={formData.email_message}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => ({ ...prev, email_message: value }));
                  setHasUnsavedChanges(true);
                }}
                placeholder="Enter the email message to send when this tool is triggered"
                className="min-h-[120px] sm:min-h-[140px] text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors"
                questions={questionsForMention}
                multiline={true}
              />
            </div>
          </div>
        );

      case 'transfer':
        return (
          <div className="space-y-5">
            {/* Transfer Preview */}
            <div className="space-y-1.5 sm:space-y-2">
              <h3 className="text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">Transfer Preview</h3>
              <div className="bg-green-50 border border-green-100 rounded-lg p-4 sm:p-5">
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 sm:p-4 relative">
                  <div className="text-xs sm:text-sm font-medium text-gray-900 mb-1">
                    {formData.transfer_name || 'Transfer Name'}
                  </div>
                  <div className="flex items-center text-xs sm:text-sm text-gray-700 mb-3">
                    <PhoneTransfer className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-green-500 mr-1.5" />
                    <span>{formData.transfer_number || 'Phone number will appear here'}</span>
                  </div>

                  {/* Operating Hours */}
                  <div className="flex items-center text-xs text-gray-600 mb-2">
                    <Clock className="h-3 w-3 text-green-500 mr-1.5" />
                    <span>{formData.transfer_start_time || '09:00'} - {formData.transfer_end_time || '17:00'}</span>
                  </div>

                  {/* Days */}
                  <div className="flex items-center text-xs text-gray-600">
                    <Calendar className="h-3 w-3 text-green-500 mr-1.5" />
                    <span>
                      {formData.transfer_days && formData.transfer_days.length > 0 
                        ? formData.transfer_days.map(dayIndex => 
                            ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dayIndex]
                          ).join(', ')
                        : 'All days'
                      }
                    </span>
                  </div>

                  <div className="absolute -top-2 -left-2 bg-green-100 text-green-800 text-[9px] sm:text-[10px] px-1 sm:px-1.5 py-0.5 rounded">
                    Preview
                  </div>
                </div>
              </div>
            </div>

            {/* Transfer Name */}
            <div className="space-y-1.5 sm:space-y-2 pt-3 sm:pt-4 border-t border-gray-100">
              <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                <PhoneTransfer className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-green-500" />
                Transfer Name
                <span className="text-red-500 ml-0.5">*</span>
              </h3>
              <p className="text-[10px] sm:text-xs text-gray-500">
                Name for this transfer (e.g., "Support Team", "Sales Department")
              </p>

              <Input
                id="transfer_name"
                name="transfer_name"
                value={formData.transfer_name}
                onChange={(e) => setFormData(prev => ({ ...prev, transfer_name: e.target.value }))}
                placeholder="Enter name for transfer"
                className="h-9 sm:h-10 text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors"
              />
            </div>

            {/* Transfer Number */}
            <div className="space-y-1.5 sm:space-y-2">
              <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                <PhoneTransfer className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-green-500" />
                Transfer Number
                <span className="text-red-500 ml-0.5">*</span>
              </h3>
              <p className="text-[10px] sm:text-xs text-gray-500">
                Enter the phone number to forward calls to when triggered
              </p>

              <Input
                id="transfer_number"
                name="transfer_number"
                value={formData.transfer_number}
                onChange={(e) => setFormData(prev => ({ ...prev, transfer_number: e.target.value }))}
                placeholder="(555) 555-5555"
                className="h-9 sm:h-10 text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors"
              />
            </div>

            {/* Operating Hours */}
            <div className="space-y-1.5 sm:space-y-2 pt-3 sm:pt-4 border-t border-gray-100">
              <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-green-500" />
                Operating Hours
                <span className="text-red-500 ml-0.5">*</span>
              </h3>
              <p className="text-[10px] sm:text-xs text-gray-500">
                Set hours when this transfer is active
              </p>

              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <div>
                  <label className="block text-[10px] sm:text-xs text-gray-500 mb-1">Start Time</label>
                  <Input
                    type="time"
                    id="transfer_start_time"
                    name="transfer_start_time"
                    value={formData.transfer_start_time}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, transfer_start_time: e.target.value }));
                      setHasUnsavedChanges(true);
                    }}
                    className="h-9 sm:h-10 text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-[10px] sm:text-xs text-gray-500 mb-1">End Time</label>
                  <Input
                    type="time"
                    id="transfer_end_time"
                    name="transfer_end_time"
                    value={formData.transfer_end_time}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, transfer_end_time: e.target.value }));
                      setHasUnsavedChanges(true);
                    }}
                    className="h-9 sm:h-10 text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Days of Week */}
            <div className="space-y-1.5 sm:space-y-2">
              <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-green-500" />
                Days of Week
                <span className="text-red-500 ml-0.5">*</span>
              </h3>
              <p className="text-[10px] sm:text-xs text-gray-500">
                Select days when this transfer is active
              </p>

              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => {
                      const currentDays = formData.transfer_days || [];
                      const newDays = currentDays.includes(index)
                        ? currentDays.filter(d => d !== index)
                        : [...currentDays, index].sort();

                      setFormData(prev => ({ ...prev, transfer_days: newDays }));
                      setHasUnsavedChanges(true);
                    }}
                    className={cn(
                      "px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[10px] sm:text-xs font-medium transition-colors",
                      formData.transfer_days?.includes(index)
                        ? "bg-green-100 text-green-800 border border-green-200"
                        : "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"
                    )}
                  >
                    {day.slice(0, 3)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'api':
        return (
          <div className="space-y-5">
            {/* API Preview */}
            <div className="space-y-1.5 sm:space-y-2">
              <h3 className="text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">API Preview</h3>
              <div className="bg-purple-50 border border-purple-100 rounded-lg p-4 sm:p-5">
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 sm:p-4 relative">
                  <div className="flex items-center mb-3">
                    <span className="text-xs sm:text-sm font-mono bg-purple-100 text-purple-800 px-2 py-1 rounded mr-2">
                      POST
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-700 truncate flex-1">
                      {formData.api_endpoint || 'https://api.example.com/endpoint'}
                    </span>
                  </div>

                  {formData.api_body && (
                    <div>
                      <div className="font-medium text-[10px] sm:text-xs text-gray-700 mb-1">Body:</div>
                      <div className="bg-gray-50 p-2 rounded-md text-[10px] sm:text-xs font-mono overflow-auto max-h-[100px]">
                        <pre className="whitespace-pre-wrap break-words">{formData.api_body}</pre>
                      </div>
                    </div>
                  )}

                  <div className="absolute -top-2 -left-2 bg-purple-100 text-purple-800 text-[9px] sm:text-[10px] px-1 sm:px-1.5 py-0.5 rounded">
                    Preview
                  </div>
                </div>
              </div>
            </div>

            {/* API Endpoint */}
            <div className="space-y-1.5 sm:space-y-2 pt-3 sm:pt-4 border-t border-gray-100">
              <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                <Globe className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-purple-500" />
                API Endpoint
                <span className="text-red-500 ml-0.5">*</span>
              </h3>
              <p className="text-[10px] sm:text-xs text-gray-500">
                Enter the URL of the API endpoint to call when triggered
              </p>

              <Input
                id="api_endpoint"
                name="api_endpoint"
                value={formData.api_endpoint}
                onChange={(e) => setFormData(prev => ({ ...prev, api_endpoint: e.target.value }))}
                placeholder="https://api.example.com/webhook"
                className="h-9 sm:h-10 text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors"
              />
            </div>

            {/* Request Body */}
            <div className="space-y-1.5 sm:space-y-2 pt-3 sm:pt-4 border-t border-gray-100">
              <h3 className="text-xs sm:text-sm font-medium text-gray-700">
                Request Body <span className="text-red-500 ml-0.5">*</span>
              </h3>
              <p className="text-[10px] sm:text-xs text-gray-500">
                JSON payload to send with the request
              </p>

              <Textarea
                id="api_body"
                name="api_body"
                value={formData.api_body}
                onChange={(e) => setFormData(prev => ({ ...prev, api_body: e.target.value }))}
                placeholder='{\n  "key": "value"\n}'
                className="min-h-[120px] sm:min-h-[140px] text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors font-mono"
                questions={questionsForMention}
                multiline={true}
              />
            </div>
          </div>
        );

      case 'dtmf':
        return (
          <div className="space-y-5">
            {/* DTMF Preview */}
            <div className="space-y-1.5 sm:space-y-2">
              <h3 className="text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">DTMF Preview</h3>
              <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 sm:p-5">
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 sm:p-4 relative">
                  <div className="flex items-center justify-center">
                    <div className="bg-gray-100 rounded-lg p-4 border border-gray-300">
                      <span className="font-mono text-xl sm:text-2xl font-bold text-gray-800">
                        {formData.dtmf_number_to_press || '___'}
                      </span>
                    </div>
                  </div>
                  <p className="text-center text-[10px] sm:text-xs text-gray-600 mt-3">
                    This will be pressed when the tool is triggered
                  </p>

                  <div className="absolute -top-2 -left-2 bg-amber-100 text-amber-800 text-[9px] sm:text-[10px] px-1 sm:px-1.5 py-0.5 rounded">
                    Preview
                  </div>
                </div>
              </div>
            </div>

            {/* Number to Press */}
            <div className="space-y-1.5 sm:space-y-2 pt-3 sm:pt-4 border-t border-gray-100">
              <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                <Hash className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-amber-500" />
                Number to Press
                <span className="text-red-500 ml-0.5">*</span>
              </h3>
              <p className="text-[10px] sm:text-xs text-gray-500">
                Enter the digit sequence to press when this tool is triggered
              </p>

              <Input
                id="dtmf_number_to_press"
                name="dtmf_number_to_press"
                value={formData.dtmf_number_to_press}
                onChange={(e) => setFormData(prev => ({ ...prev, dtmf_number_to_press: e.target.value }))}
                placeholder="e.g., 1, 123, *0#"
                className="h-9 sm:h-10 text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors font-mono"
              />

              <div className="flex items-center space-x-2 text-[10px] sm:text-xs text-gray-500">
                <span>Examples:</span>
                <code className="bg-gray-100 px-1 rounded">1</code>
                <code className="bg-gray-100 px-1 rounded">*0#</code>
                <code className="bg-gray-100 px-1 rounded">123</code>
              </div>
            </div>
          </div>
        );

      case 'note':
        return (
          <NoteFieldsTab
            formData={formData}
            updateNoteFieldsConfig={updateNoteFieldsConfig}
            validationErrors={validationErrors.note_fields_individual || {}}
            setValidationErrors={setValidationErrors}
            setHasUnsavedChanges={setHasUnsavedChanges}
            isMobile={isMobile}
          />
        );

      default:
        return (
          <div className="text-center py-8 text-gray-500">
            <Wrench className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="font-medium">Unknown Action Type: {actionType}</p>
            <p className="text-sm">Please check the action configuration.</p>
            <div className="mt-4 p-3 bg-gray-50 rounded text-xs text-left">
              <strong>Debug Info:</strong>
              <pre>{JSON.stringify(variant, null, 2)}</pre>
            </div>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  const ActionIcon = getActionIcon();

  const renderContent = () => (
    <motion.div 
      className={cn(
        "bg-gray-50 flex flex-col overflow-hidden",
        isInline 
          ? "w-full h-full" 
          : "rounded-2xl shadow-2xl w-full max-w-2xl mx-2 sm:mx-4 relative border border-gray-200/50"
      )}
      variants={!isInline ? modalVariants : {}}
      initial={!isInline ? "hidden" : ""}
      animate={!isInline ? "visible" : ""}
      exit={!isInline ? "exit" : ""}
      onClick={e => e.stopPropagation()}
    >
      {/* Elegant Header with Gradient */}
      <motion.div 
        className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200/50 flex justify-between items-center bg-white/70 backdrop-blur-sm z-20 flex-shrink-0"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.3 }}
      >
        <div className="flex items-center">
          <motion.div 
            className="w-10 h-10 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-600 shadow-inner flex items-center justify-center mr-3"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
          >
            <ActionIcon className="h-5 w-5 text-white" />
          </motion.div>
          <div>
            <h2 className="text-lg sm:text-xl font-light text-gray-900 tracking-tight">
              {getActionTitle()}: {variant?.name}
            </h2>
            <p className="text-sm text-gray-500 font-light">
              {categoryName ? `${categoryName} - Location Variant` : `${variant?.parentSetting?.name} - Location Variant`}
            </p>
          </div>
        </div>
        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Button
            type="button"
            disabled={isSubmitting}
            onClick={handleSave}
            size="sm"
            className="bg-gradient-to-r from-gray-900 to-gray-800 hover:from-gray-800 hover:to-gray-700 text-white min-w-[100px] shadow-inner rounded-xl"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-1">
                <svg className="animate-spin h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-xs sm:text-sm font-light">Saving</span>
              </span>
            ) : (
              <span className="text-xs sm:text-sm font-light">Save</span>
            )}
          </Button>
        </motion.div>
      </motion.div>

      {/* Content with Enhanced Styling */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
        >
          {renderActionFields()}
        </motion.div>
      </div>
    </motion.div>
  );

  if (isInline) {
    return renderContent();
  }

  return (
    <motion.div 
      className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[10001] flex items-start justify-center overflow-auto pt-2 sm:pt-10 md:pt-20 pb-4 sm:pb-10"
      variants={backdropVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      onClick={onClose}
    >
      {renderContent()}
    </motion.div>
  );
};

const NoteFieldsTab = ({
  formData,
  updateNoteFieldsConfig,
  validationErrors,
  setValidationErrors,
  setHasUnsavedChanges,
  isMobile
}) => {
  const [questionText, setQuestionText] = useState('');
  const [questionType, setQuestionType] = useState('string');
  const [options, setOptions] = useState([]);
  const [newOption, setNewOption] = useState('');
  const [editingFieldTempId, setEditingFieldTempId] = useState(null);

  const [isFollowUp, setIsFollowUp] = useState(false);
  const [parentFieldId, setParentFieldId] = useState('');
  const [parentFieldTriggerRule, setParentFieldTriggerRule] = useState({ type: 'answered', value: undefined });
  const [selectedParentFieldDetails, setSelectedParentFieldDetails] = useState(null);

  const noteFields = formData.note_fields_config || [];

  const generateTempId = () => `temp_note_field_${Math.random().toString(36).substr(2, 9)}`;

  useEffect(() => {
    if (parentFieldId) {
      const parentField = noteFields.find(f => f.temp_id === parentFieldId);
      setSelectedParentFieldDetails(parentField ? { temp_id: parentField.temp_id, type: parentField.type, options: parentField.options || [], question: parentField.question } : null);
      setParentFieldTriggerRule({ type: 'answered', value: undefined });
    } else {
      setSelectedParentFieldDetails(null);
      setParentFieldTriggerRule({ type: 'answered', value: undefined });
    }
  }, [parentFieldId, noteFields]);

  const handleAddOrUpdateField = () => {
    if (!questionText.trim()) {
      toast.error('Field label cannot be empty.');
      return;
    }
    if (questionType === 'enum' && options.length === 0) {
      toast.error('Please add at least one option for "Choose One" fields.');
      return;
    }

    let fieldCriteria = null;
    if (isFollowUp && parentFieldId && selectedParentFieldDetails) {
        let dbCondition = '';
        let dbValue = parentFieldTriggerRule.value;

        const triggers = [];
        switch (parentFieldTriggerRule.type) {
            case 'answered':
                dbCondition = 'answered';
                dbValue = undefined;
                break;
            case 'not_answered':
                dbCondition = 'not_answered';
                dbValue = undefined;
                break;
            case 'matches_specific_value':
                switch (selectedParentFieldDetails.type) {
                    case 'enum':
                        dbCondition = 'equals_any_of';
                        dbCondition = 'equals';
                        dbValue = parentFieldTriggerRule.value;
                        break;
                    case 'boolean':
                        dbCondition = 'equals';
                        dbValue = parentFieldTriggerRule.value;
                        break;
                    default:
                        dbCondition = 'equals';
                        dbValue = parentFieldTriggerRule.value;
                        break;
                }
                break;
            default: break;
        }

        if (dbCondition) {
            triggers.push({
                parent_temp_id: parentFieldId,
                condition: dbCondition,
                value: dbValue,
            });
        }
        if (triggers.length > 0) {
            fieldCriteria = { triggers };
        }
    }

    const newField = {
      temp_id: editingFieldTempId || generateTempId(),
      id: editingFieldTempId ? noteFields.find(f => f.temp_id === editingFieldTempId)?.id : undefined,
      question: questionText,
      type: questionType,
      options: questionType === 'enum' ? options : [],
      order: 0,
      parent_field_temp_id: isFollowUp ? parentFieldId : null,
      criteria: fieldCriteria
    };

    let updatedFields;
    if (editingFieldTempId !== null) {
      const fieldIdx = noteFields.findIndex(f => f.temp_id === editingFieldTempId);
      if (fieldIdx === -1) {
        console.error("Editing field not found by temp_id", editingFieldTempId);
        toast.error("Error updating field.");
        resetForm();
        return;
      }
      newField.order = noteFields[fieldIdx].order;
      updatedFields = noteFields.map(f => (f.temp_id === editingFieldTempId ? newField : f));
      toast.success('Field updated');
    } else {
      if (newField.parent_field_temp_id) {
          const siblings = noteFields.filter(f => f.parent_field_temp_id === newField.parent_field_temp_id);
          newField.order = siblings.length;
      } else {
          const topLevelFields = noteFields.filter(f => !f.parent_field_temp_id);
          newField.order = topLevelFields.length;
      }
      updatedFields = [...noteFields, newField];
      toast.success('Field added');
    }

    const reOrderFields = (fields) => {
        const topLevel = fields.filter(f => !f.parent_field_temp_id).sort((a,b) => a.order - b.order);
        const childrenMap = fields.filter(f => f.parent_field_temp_id).reduce((acc, f) => {
            if (!acc[f.parent_field_temp_id]) acc[f.parent_field_temp_id] = [];
            acc[f.parent_field_temp_id].push(f);
            return acc;
        }, {});

        let globalOrder = 0;
        const result = [];

        function processLevel(items) {
            items.forEach(item => {
                result.push({ ...item, order: globalOrder++ });
                if (childrenMap[item.temp_id]) {
                    const sortedChildren = childrenMap[item.temp_id].sort((a,b) => a.order - b.order);
                    processLevel(sortedChildren);
                }
            });
        }
        processLevel(topLevel);
        return result;
    };

    updateNoteFieldsConfig(reOrderFields(updatedFields));
    resetForm();
  };

  const resetForm = () => {
    setQuestionText('');
    setQuestionType('string');
    setOptions([]);
    setNewOption('');
    setEditingFieldTempId(null);
    setIsFollowUp(false);
    setParentFieldId('');
    setSelectedParentFieldDetails(null);
    setParentFieldTriggerRule({ type: 'answered', value: undefined });

    if (editingFieldTempId && validationErrors[editingFieldTempId]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[editingFieldTempId];
        if (Object.keys(newErrors).length === 0 && prev.note_fields_tab_error) {
          delete newErrors.note_fields_tab_error;
        }
        return newErrors;
      });
    }
  };

  const handleEditField = (fieldToEdit) => {
    setQuestionText(fieldToEdit.question);
    setQuestionType(fieldToEdit.type);
    setOptions(fieldToEdit.options || []);
    setNewOption('');
    setEditingFieldTempId(fieldToEdit.temp_id);

    if (fieldToEdit.parent_field_temp_id) {
      setIsFollowUp(true);
      setParentFieldId(fieldToEdit.parent_field_temp_id);
      if (fieldToEdit.criteria && fieldToEdit.criteria.triggers && fieldToEdit.criteria.triggers.length > 0) {
        const critTrigger = fieldToEdit.criteria.triggers[0];
        let ruleType = 'answered';
        if (critTrigger.condition === 'answered' || critTrigger.condition === 'not_answered') {
            ruleType = critTrigger.condition;
        } else if (critTrigger.condition === 'equals' || critTrigger.condition === 'equals_any_of') {
            ruleType = 'matches_specific_value';
        }
        setParentFieldTriggerRule({ type: ruleType, value: critTrigger.value });
      } else {
        setParentFieldTriggerRule({ type: 'answered', value: undefined });
      }
    } else {
      setIsFollowUp(false);
      setParentFieldId('');
      setParentFieldTriggerRule({ type: 'answered', value: undefined });
    }
  };

  const handleDeleteField = (fieldToDeleteTempId) => {
    let fieldsAfterDelete = noteFields.filter(f => f.temp_id !== fieldToDeleteTempId);
    fieldsAfterDelete = fieldsAfterDelete.filter(f => f.parent_field_temp_id !== fieldToDeleteTempId);

    const reOrderFields = (fields) => {
        const topLevel = fields.filter(f => !f.parent_field_temp_id).sort((a,b) => a.order - b.order);
        const childrenMap = fields.filter(f => f.parent_field_temp_id).reduce((acc, f) => {
            if (!acc[f.parent_field_temp_id]) acc[f.parent_field_temp_id] = [];
            acc[f.parent_field_temp_id].push(f);
            return acc;
        }, {});
        let globalOrder = 0;
        const result = [];
        function processLevel(items) {
            items.forEach(item => {
                result.push({ ...item, order: globalOrder++ });
                if (childrenMap[item.temp_id]) {
                    const sortedChildren = childrenMap[item.temp_id].sort((a,b) => a.order - b.order);
                    processLevel(sortedChildren);
                }
            });
        }
        processLevel(topLevel);
        return result;
    };

    updateNoteFieldsConfig(reOrderFields(fieldsAfterDelete));

    if (editingFieldTempId === fieldToDeleteTempId) {
      resetForm();
    }
    toast.success('Field deleted');
  };

  const handleAddOption = () => {
    if (newOption.trim()) {
      setOptions(prev => [...prev, newOption.trim()]);
      setNewOption('');
      setHasUnsavedChanges(true);
    }
  };

  const handleRemoveOption = (optionIndex) => {
    setOptions(prev => prev.filter((_, idx) => idx !== optionIndex));
    setHasUnsavedChanges(true);
  };

  const moveField = (fieldTempId, direction) => {
    const fields = [...noteFields];
    const fieldIndex = fields.findIndex(f => f.temp_id === fieldTempId);
    if (fieldIndex === -1) return;

    const fieldToMove = fields[fieldIndex];
    const siblings = fields.filter(f => f.parent_field_temp_id === fieldToMove.parent_field_temp_id);
    const currentIndexInSiblings = siblings.findIndex(s => s.temp_id === fieldTempId);

    let targetIndexInSiblings;
    if (direction === 'up') {
        if (currentIndexInSiblings === 0) return;
        targetIndexInSiblings = currentIndexInSiblings - 1;
    } else {
        if (currentIndexInSiblings === siblings.length - 1) return;
        targetIndexInSiblings = currentIndexInSiblings + 1;
    }

    const otherSibling = siblings[targetIndexInSiblings];
    const fieldToMoveOriginalOrder = fieldToMove.order;
    fieldToMove.order = otherSibling.order;
    otherSibling.order = fieldToMoveOriginalOrder;

    const sortedFields = fields.sort((a, b) => {
        if (a.parent_field_temp_id !== b.parent_field_temp_id) {
            return (a.parent_field_temp_id || '').localeCompare(b.parent_field_temp_id || '');
        }
        return a.order - b.order;
    });

    updateNoteFieldsConfig(sortedFields);
    setHasUnsavedChanges(true);
  };

  const getEligibleParentFields = () => {
    return noteFields.filter(f => {
      if (editingFieldTempId && f.temp_id === editingFieldTempId) return false;
      if (editingFieldTempId && f.parent_field_temp_id === editingFieldTempId) return false;
      return true;
    });
  };

  const getHierarchicalNoteFieldsForDisplay = () => {
    const fields = [...(formData.note_fields_config || [])];
    const fieldMap = new Map(fields.map(f => [f.temp_id, { ...f, children: [] }]));
    const topLevelFields = [];

    for (const field of fields) {
        const mappedField = fieldMap.get(field.temp_id);
        if (field.parent_field_temp_id && fieldMap.has(field.parent_field_temp_id)) {
            const parent = fieldMap.get(field.parent_field_temp_id);
            if (parent) parent.children.push(mappedField);
        } else {
            topLevelFields.push(mappedField);
        }
    }

    fieldMap.forEach(field => {
        if (field.children.length > 0) {
            field.children.sort((a, b) => (a.order || 0) - (b.order || 0));
        }
    });
    topLevelFields.sort((a, b) => (a.order || 0) - (b.order || 0));

    const displayList = [];
    function flatten(itemsToFlatten, depth) {
        for (const item of itemsToFlatten) {
            displayList.push({ ...item, depth });
            if (item.children.length > 0) {
                flatten(item.children, depth + 1);
            }
        }
    }
    flatten(topLevelFields, 0);
    return displayList;
  };
  const displayableFields = getHierarchicalNoteFieldsForDisplay();

  return (
    <div className="space-y-6 py-3 sm:py-4">
      <div className="space-y-4">
        <div>
          <h3 className="text-sm sm:text-base font-medium text-gray-800">
            {editingFieldTempId ? 'Edit Note Field' : 'Add Note Field'}
          </h3>
          <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">
            Define the fields for taking notes.
          </p>
        </div>
        <div className="space-y-2">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
            <Input
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              placeholder="Field Label (e.g., Customer Name, Issue)"
              className="flex-1 h-9 sm:h-10 text-sm border-gray-200"
            />
            <Select value={questionType} onValueChange={(value) => {
                setQuestionType(value);
                if (value !== 'enum') setOptions([]);
            }}>
              <SelectTrigger className="w-full sm:w-48 h-9 sm:h-10 text-xs sm:text-sm border-gray-200">
                <SelectValue placeholder="Field Type" />
              </SelectTrigger>
              <SelectContent className="z-[10003]">
                <SelectItem value="string">Text</SelectItem>
                <SelectItem value="numeric">Number</SelectItem>
                <SelectItem value="boolean">Yes/No</SelectItem>
                <SelectItem value="enum">Choose One</SelectItem>
              </SelectContent>
            </Select>
            <Button
              type="button"
              onClick={handleAddOrUpdateField}
              disabled={!questionText.trim()}
              className="whitespace-nowrap text-xs sm:text-sm px-2.5 sm:px-3"
              size={isMobile ? "sm" : "default"}
            >
              <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-0.5 sm:mr-1" />
              {editingFieldTempId !== null ? 'Update Field' : 'Add Field'}
            </Button>
            {editingFieldTempId !== null && (
                <Button
                    type="button"
                    variant="ghost"
                    onClick={resetForm}
                    className="text-xs sm:text-sm"
                    size={isMobile ? "sm" : "default"}
                >
                    Cancel Edit
                </Button>
            )}
          </div>
           {validationErrors && validationErrors[editingFieldTempId || 'new_field_general'] && (
            <motion.p
                className="text-[11px] sm:text-xs text-red-500 flex items-center mt-1"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
            >
                {validationErrors[editingFieldTempId || 'new_field_general']}
            </motion.p>
           )}
        </div>

        {questionType === 'enum' && (
          <motion.div
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            className="mt-3 border border-gray-200 rounded-lg p-3 bg-gray-50/50"
          >
            <Label className="text-xs font-medium text-gray-700 mb-2 block">
              Options for "Choose One"
            </Label>
            <div className="flex space-x-2 mb-3">
              <Input
                value={newOption}
                onChange={(e) => setNewOption(e.target.value)}
                placeholder="Add an option"
                className="flex-1 h-8 text-sm border-gray-200 bg-white"
                onKeyPress={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddOption(); } }}
              />
              <Button onClick={handleAddOption} variant="outline" size="sm" disabled={!newOption.trim()}>
                Add Option
              </Button>
            </div>
            <div className="bg-white border border-gray-200 rounded-md p-2 max-h-[120px] overflow-y-auto">
              {options.length > 0 ? (
                options.map((opt, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex justify-between items-center py-1.5 px-2 hover:bg-gray-50 rounded text-xs"
                  >
                    <span>{opt}</span>
                    <button onClick={() => handleRemoveOption(idx)} className="text-gray-400 hover:text-red-500">
                                              <Trash size={14} />
                    </button>
                  </motion.div>
                ))
              ) : (
                <p className="text-gray-400 text-xs py-2 text-center">No options added yet.</p>
              )}
            </div>
          </motion.div>
        )}

        <div className="pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                    <h4 className="text-xs sm:text-sm font-medium text-gray-700">Follow-up Question</h4>
                    <p className="text-[10px] sm:text-xs text-gray-500">
                        Show this field only if another field is answered a certain way.
                    </p>
                </div>
                <Switch
                    checked={isFollowUp}
                    onCheckedChange={(checked) => {
                        setIsFollowUp(checked);
                        if (!checked) {
                            setParentFieldId('');
                            setParentFieldTriggerRule({ type: 'answered', value: undefined });
                        }
                    }}
                    className="data-[state=checked]:bg-purple-500"
                    disabled={editingFieldTempId !== null && noteFields.some(f => f.parent_field_temp_id === editingFieldTempId)}
                />
            </div>
        </div>

        {isFollowUp && (
            <motion.div
                initial={{ opacity: 0, y: -10, height: 0 }}
                animate={{ opacity: 1, y: 0, height: 'auto' }}
                className="mt-2 space-y-3 border border-gray-200 rounded-lg p-3 bg-gray-50/50"
            >
                <div>
                    <Label htmlFor="parentField" className="text-xs font-medium text-gray-700 mb-1 block">
                        Show when field:
                    </Label>
                    <Select
                        value={parentFieldId}
                        onValueChange={setParentFieldId}
                    >
                        <SelectTrigger id="parentField" className="h-9 sm:h-10 text-sm border-gray-200 bg-white">
                            <SelectValue placeholder="Select parent field" />
                        </SelectTrigger>
                        <SelectContent className="z-[10004]">
                            {getEligibleParentFields().length === 0 && (
                                <SelectItem value="" disabled className="text-xs text-gray-400">No eligible parent fields</SelectItem>
                            )}
                            {getEligibleParentFields().map(f => (
                                <SelectItem key={f.temp_id} value={f.temp_id} className="text-sm">
                                    {f.question} ({f.type})
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {parentFieldId && selectedParentFieldDetails && (
                    <>
                        <div className="mt-2 space-y-1">
                            <Label className="text-xs font-medium text-gray-700 mb-1 block">
                                This field should appear when "{selectedParentFieldDetails.question}"...
                            </Label>
                            <Select
                                value={parentFieldTriggerRule.type}
                                onValueChange={(type) => {
                                    let defaultValue;
                                    if (type === 'matches_specific_value') {
                                        if (selectedParentFieldDetails.type === 'enum') defaultValue = '';
                                        else if (selectedParentFieldDetails.type === 'boolean') defaultValue = 'true';
                                        else defaultValue = '';
                                    } else {
                                        defaultValue = undefined;
                                    }
                                    setParentFieldTriggerRule({ type: type, value: defaultValue });
                                }}
                            >
                                <SelectTrigger className="h-9 sm:h-10 text-sm border-gray-200 bg-white">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="z-[10004]">
                                    <SelectItem value="answered">Is Answered</SelectItem>
                                    {(selectedParentFieldDetails.type === 'enum' || selectedParentFieldDetails.type === 'boolean' || selectedParentFieldDetails.type === 'string' || selectedParentFieldDetails.type === 'numeric') && (
                                        <SelectItem value="matches_specific_value">Matches Specific Value</SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>

                        {parentFieldTriggerRule.type === 'matches_specific_value' && (
                            <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                                <Label className="text-xs font-medium text-gray-700 mb-0.5 block">
                                    Specify the value it should match:
                                </Label>
                                {selectedParentFieldDetails.type === 'enum' && (
                                    <Select
                                        value={parentFieldTriggerRule.value || ''}
                                        onValueChange={(val) => setParentFieldTriggerRule({ ...parentFieldTriggerRule, value: val })}
                                    >
                                        <SelectTrigger className="h-9 sm:h-10 text-sm border-gray-200 bg-white">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="z-[10004]">
                                            {(selectedParentFieldDetails.options || []).map(opt => (
                                                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                                {selectedParentFieldDetails.type === 'boolean' && (
                                    <Select
                                        value={String(parentFieldTriggerRule.value === undefined ? true : parentFieldTriggerRule.value)}
                                        onValueChange={(val) => setParentFieldTriggerRule({ ...parentFieldTriggerRule, value: val === 'true' })}
                                    >
                                        <SelectTrigger className="h-9 sm:h-10 text-sm border-gray-200 bg-white">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="z-[10004]">
                                            <SelectItem value="true">Yes</SelectItem>
                                            <SelectItem value="false">No</SelectItem>
                                        </SelectContent>
                                    </Select>
                                )}
                                {(selectedParentFieldDetails.type === 'string' || selectedParentFieldDetails.type === 'numeric') && (
                                    <Input
                                        type={selectedParentFieldDetails.type === 'numeric' ? 'number' : 'text'}
                                        value={parentFieldTriggerRule.value || ''}
                                        onChange={(e) => setParentFieldTriggerRule({ ...parentFieldTriggerRule, value: e.target.value })}
                                        placeholder={`Enter expected ${selectedParentFieldDetails.type} value`}
                                        className="h-9 sm:h-10 text-sm border-gray-200 bg-white"
                                    />
                                )}
                            </div>
                        )}
                    </>
                )}
            </motion.div>
        )}
      </div>

      <div className="mt-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center mb-1 sm:mb-2">
                              <Question size={isMobile ? 14 : 16} className="text-gray-600 mr-1.5 sm:mr-2" />
              <span className="text-xs text-gray-600 font-medium">Note Fields Preview</span>
            </div>
            <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">
              Fields will be presented in this order. Use arrows to reorder.
            </p>
          </div>
          {displayableFields.length > 0 && (
            <div className="text-[10px] sm:text-xs text-gray-500 bg-gray-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">
              {displayableFields.length} {displayableFields.length === 1 ? 'field' : 'fields'}
            </div>
          )}
        </div>

        <div className="min-h-[150px] bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200 relative mt-2">
          {displayableFields.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-6 sm:py-8">
              <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-gray-300 mb-1.5 sm:mb-2" />
              <p className="text-xs sm:text-sm text-gray-400">No fields defined for this note.</p>
              <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5 sm:mt-1">Add fields above to structure the note.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {displayableFields.map((field, displayIdx) => (
                <motion.div
                  key={field.temp_id}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
                  style={{ marginLeft: `${field.depth * 20}px` }}
                >
                  <div className="flex items-center border-b border-gray-100 px-3 py-2">
                    <div className="flex-shrink-0 mr-2 flex items-center">
                       <span className="text-xs font-medium text-gray-500 bg-gray-100 rounded-full w-5 h-5 flex items-center justify-center">
                        {field.order + 1}
                      </span>
                    </div>
                    <div className="flex-1 flex items-center overflow-hidden">
                      <div className={`truncate text-xs px-2 py-0.5 rounded ${
                        field.type === 'boolean' ? 'bg-green-100 text-green-800' :
                        field.type === 'enum' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {field.type === 'string' ? 'Text' :
                         field.type === 'numeric' ? 'Number' :
                         field.type === 'boolean' ? 'Yes/No' :
                         field.type === 'enum' ? 'Choose One' : 'Unknown'}
                      </div>
                       {field.parent_field_temp_id && (
                        <span className="ml-2 text-[10px] text-purple-600 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded-sm">
                            Follow-up
                        </span>
                       )}
                    </div>
                    <div className="flex items-center space-x-0.5">
                      <button
                        type="button"
                        onClick={() => moveField(field.temp_id, 'up')}
                        className="p-1 text-gray-400 hover:text-gray-600 focus:outline-none disabled:opacity-50"
                        disabled={field.order === 0}
                       >
                        <CaretUp size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveField(field.temp_id, 'down')}
                        className="p-1 text-gray-400 hover:text-gray-600 focus:outline-none disabled:opacity-50"
                        disabled={field.order === (noteFields.filter(f => f.parent_field_temp_id === field.parent_field_temp_id).length -1) }
                      >
                        <CaretDown size={16} />
                      </button>
                      <button type="button" onClick={() => handleEditField(field)} className="p-1 text-gray-400 hover:text-blue-500 focus:outline-none">
                        <Gear className="h-3.5 w-3.5" />
                      </button>
                      <button type="button" onClick={() => handleDeleteField(field.temp_id)} className="p-1 text-gray-400 hover:text-red-500 focus:outline-none">
                        <Trash className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="px-3 py-2.5">
                    <p className="text-sm text-gray-800">{field.question}</p>
                    {field.type === 'enum' && field.options && field.options.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {field.options.map((opt, optIdx) => (
                          <span key={optIdx} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                            {opt}
                          </span>
                        ))}
                      </div>
                    )}
                    {field.parent_field_temp_id && field.criteria && field.criteria.triggers && field.criteria.triggers.length > 0 && (
                        <div className="mt-1.5 text-[10px] text-gray-500 border-t border-gray-100 pt-1.5">
                           <span className="font-medium">Shows if:</span> {
                               noteFields.find(f => f.temp_id === field.parent_field_temp_id)?.question || 'Parent field'
                           }
                           <span className="text-purple-600 ml-1">
                                ({field.criteria.triggers[0].condition}
                                {field.criteria.triggers[0].value !== undefined ? ` '${String(field.criteria.triggers[0].value)}'` : ''})
                           </span>
                        </div>
                    )}
                     {validationErrors && validationErrors[field.temp_id] && (
                        <motion.p
                            className="text-[10px] text-red-500 mt-1"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                        >
                           {validationErrors[field.temp_id]}
                        </motion.p>
                     )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 